import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CalendarPlus, ChevronLeft, ChevronRight, Plus, Ban } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { bookingTitle, fmtDate, fmtRange, monthLabel, monthOf, shiftMonth, todayIn } from "@web/lib/format";
import { useNavAction } from "@web/lib/navAction";
import { api, calendarQuery, venuesQuery, type CalendarBlock, type CalendarBooking } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { addDays } from "@shared/dates";
import { recordValidationQuery, useBatchValidation } from "@web/lib/validation";
import { reservationMode } from "@web/lib/accessPhases";
import { ValidationSummary } from "@web/ui";
import { BottomSheet, Button, ChipStrip, EmptyState, EventRow, Inline, MonthGrid, NavBarAction, Page, Row, RowGroup, SelectChip, Skeleton, Stack, Text, TourRow, Workspace, Region, monthCells, useToast, type MonthBar } from "@web/ui";
import { BlockSheet } from "./sheets/BlockSheet";
import { NewBookingSheet } from "./sheets/NewBookingSheet";

const barStyle = (b: CalendarBooking): MonthBar["style"] => (b.status === "pending" ? b.blocks && b.holdStatus === "confirmed" ? "striped" : "outline" : "solid");

export type CalendarSearch = { month?: string; day?: string; venue?: string };

/** The shareable address contract: unknown keys and malformed dates are dropped,
 * and an absent key means the screen's own default. */
export function parseCalendarSearch(raw: Record<string, unknown>): CalendarSearch {
  const out: CalendarSearch = {};
  if (typeof raw.month === "string" && /^\d{4}-\d{2}$/.test(raw.month)) out.month = raw.month;
  if (typeof raw.day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.day)) out.day = raw.day;
  if (typeof raw.venue === "string" && raw.venue && raw.venue.length <= 200) out.venue = raw.venue;
  return out;
}

/** Calendar (SPEC §6.1 item 6): month grid + agenda for the tapped day; venue color chips filter. */
export function Calendar() {
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const today = todayIn(me.organization.timezone);
  // Month, selected day and venue live in the URL, so opening an event in March
  // and pressing Back comes back to March (docs/plans/RESPONSIVE-STANDARD.md).
  const search = useSearch({ from: "/app/calendar" });
  const month = search.month ?? monthOf(today);
  const selected = search.day ?? today;
  const venueId = search.venue ?? null;
  const setSearch = (next: CalendarSearch) => navigate({ to: "/calendar", search: { ...search, ...next }, replace: true });
  const [sheet, setSheet] = useState<"add" | "booking" | "block" | null>(null);
  const [block, setBlock] = useState<CalendarBlock | null>(null);
  useNavAction({ label: "Add", icon: <Plus aria-hidden size={26} />, onClick: () => setSheet("add") });

  const venues = useQuery(venuesQuery());
  const cells = useMemo(() => monthCells(month), [month]);
  const range = { from: cells[0].date, to: cells[cells.length - 1].date, venueId };
  const cal = useQuery(calendarQuery(range));
  const checks = useBatchValidation([...(cal.data?.bookings ?? []).map(row => ({ entityType: "booking" as const, id: row.id })), ...(cal.data?.tours ?? []).map(row => ({ entityType: "tour" as const, id: row.id }))]);
  const blockValidation = useQuery(recordValidationQuery("calendar_block", block?.id ?? ""));
  const venueById = useMemo(() => new Map((venues.data ?? []).map((v) => [v.id, v])), [venues.data]);

  function changeMonth(by: number) {
    const next = shiftMonth(month, by);
    // Keep the agenda inside its loaded month. Returning to this month restores
    // today; other months start on day one, including December/January changes.
    setSearch({ month: next, day: next === monthOf(today) ? today : `${next}-01` });
  }

  const bars = useMemo<MonthBar[]>(() => {
    const out: MonthBar[] = [];
    for (const b of cal.data?.bookings ?? []) {
      const common = { color: venueById.get(b.venueId)?.color, style: barStyle(b) }, name = b.eventName || bookingTitle(b);
      if (b.unknownBounds || reservationMode(b) === "legacy_unresolved" || !b.eventEndDate) out.push({ ...common, key: `b${b.id}`, from: b.spanStart, to: b.spanEnd, label: `${b.unknownBounds ? "Unresolved access" : reservationMode(b) === "legacy_unresolved" ? "Legacy protected access" : "Access — event end unknown"} · ${name}` });
      else {
        if (b.spanStart < b.eventDate) out.push({ ...common, key: `b${b.id}-setup`, from: b.spanStart, to: addDays(b.eventDate, -1), label: `Setup · ${name}` });
        out.push({ ...common, key: `b${b.id}`, from: b.eventDate, to: b.eventEndDate, label: `Event · ${name}` });
        if (b.spanEnd > b.eventEndDate) out.push({ ...common, key: `b${b.id}-cleanup`, from: addDays(b.eventEndDate, 1), to: b.spanEnd, label: `Cleanup · ${name}` });
      }
    }
    for (const k of cal.data?.blocks ?? []) out.push({ key: `k${k.id}`, from: !k.accessStartUtc || !k.accessEndUtc ? range.from : k.startDate, to: !k.accessStartUtc || !k.accessEndUtc ? range.to : k.endDate, style: "grey", label: !k.accessStartUtc || !k.accessEndUtc ? "Unresolved closure — review dates" : k.note || (k.kind === "maintenance" ? "Maintenance" : "Closed") });
    for (const t of cal.data?.tours ?? []) if (t.status === "scheduled") out.push({ key: `t${t.id}`, from: t.spanStart ?? t.scheduledAt.slice(0, 10), to: t.spanEnd ?? t.scheduledAt.slice(0, 10), color: t.venueColor, style: "dot", label: `Tour · ${[t.card.line1, t.card.line2, t.card.line3].filter(Boolean).join(" · ")}` });
    return out;
  }, [cal.data, venueById, range.from, range.to]);

  const dayBookings = (cal.data?.bookings ?? []).filter((b) => selected && b.spanStart <= selected && b.spanEnd >= selected).sort((a, b) => (a.accessStartUtc ?? "").localeCompare(b.accessStartUtc ?? "") || a.id.localeCompare(b.id));
  const dayBlocks = (cal.data?.blocks ?? []).filter((k) => selected && (!k.accessStartUtc || !k.accessEndUtc || (k.startDate <= selected && k.endDate >= selected)));
  const dayTours = (cal.data?.tours ?? []).filter((t) => selected && (t.spanStart ?? t.scheduledAt.slice(0, 10)) <= selected && (t.spanEnd ?? t.scheduledAt.slice(0, 10)) >= selected && t.status !== "cancelled");

  async function removeBlock() {
    if (!block) return;
    try {
      await api.deleteBlock(block.id);
      toast.success("Block removed");
      setBlock(null);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <Page>
      <Workspace variant="calendar">
        <Region area="header"><Stack gap={4}>
        <Inline justify="between">
          <NavBarAction label="Previous month" icon={<ChevronLeft aria-hidden size={24} />} onClick={() => changeMonth(-1)} />
          <Text variant="headline" as="h2">
            {monthLabel(month)}
          </Text>
          <NavBarAction label="Next month" icon={<ChevronRight aria-hidden size={24} />} onClick={() => changeMonth(1)} />
        </Inline>
        {venues.data && venues.data.length > 1 && (
          <ChipStrip label="Venues">
            <SelectChip selected={venueId === null} onClick={() => setSearch({ venue: undefined })}>
              All venues
            </SelectChip>
            {venues.data.map((v) => (
              <SelectChip key={v.id} color={v.color} selected={venueId === v.id} onClick={() => setSearch({ venue: venueId === v.id ? undefined : v.id })}>
                {v.shortName}
              </SelectChip>
            ))}
          </ChipStrip>
        )}
        </Stack></Region>
        <Region area="main">
        {cal.isPending ? <Skeleton variant="cards" count={3} /> : cal.isError ? <EmptyState title="Unable to load calendar" body={errorMessage(cal.error)} action={<Button variant="secondary" onClick={() => cal.refetch()}>Try again</Button>} /> : <MonthGrid month={month} bars={bars} selected={selected} today={today} onSelect={(date) => setSearch({ day: date })} />}
        </Region>
        <Region area="aside" label="Selected day">
        <Stack gap={2}>
          <Text variant="headline">{selected ? fmtDate(selected, { weekday: true }) : "Pick a day"}</Text>
          {cal.isPending ? <Stack gap={2}><Text variant="callout" tone="secondary">Loading agenda…</Text><Skeleton count={2} /></Stack> : cal.isError ? <Text variant="callout" tone="secondary">The agenda is unavailable until the calendar loads.</Text> : <>
          {selected && dayBookings.length === 0 && dayBlocks.length === 0 && dayTours.length === 0 && (
            <EmptyState
              title="Nothing on this day"
              action={
                <Button variant="secondary" fullWidth={false} icon={<CalendarPlus aria-hidden size={18} />} onClick={() => setSheet("booking")}>
                  New booking
                </Button>
              }
            />
          )}
          {(dayBookings.length > 0 || dayBlocks.length > 0 || dayTours.length > 0) && (
            <RowGroup>
              {dayBookings.map((b) => <EventRow key={b.id} event={b} showDate={false} operationalDate={selected} validation={checks.data?.records.find(record => record.entity.type === "booking" && record.entity.id === b.id)} checksUnavailable={checks.isError} onClick={() => navigate({ to: "/bookings/$id", params: { id: b.id } })} />)}
              {dayTours.map((t) => (
                <TourRow key={t.id} tour={t} showDate={false} operationalDate={selected} validation={checks.data?.records.find(record => record.entity.type === "tour" && record.entity.id === t.id)} checksUnavailable={checks.isError} onClick={t.leadId ? () => navigate({ to: "/leads/$id", params: { id: t.leadId! } }) : undefined} />
              ))}
              {dayBlocks.map((k) => (
                <Row key={k.id} icon={<Ban aria-hidden size={22} />} title={!k.accessStartUtc || !k.accessEndUtc ? "Unresolved closure — review dates" : k.kind === "maintenance" ? "Maintenance" : "Closed"} subtitle={`${venueById.get(k.venueId)?.shortName ?? ""} · ${fmtRange(k.startDate, k.endDate)}${k.note ? ` · ${k.note}` : ""}`} onClick={() => setBlock(k)} />
              ))}
            </RowGroup>
          )}
          </>}
        </Stack>
        </Region>
      </Workspace>

      <BottomSheet open={sheet === "add"} onClose={() => setSheet(null)} title="Add" size="small">
        <RowGroup>
          <Row icon={<CalendarPlus aria-hidden size={22} />} title="New booking" subtitle="Venue, package, contact, dates" onClick={() => setSheet("booking")} />
          <Row icon={<Ban aria-hidden size={22} />} title="Block dates" subtitle="Closed or maintenance" onClick={() => setSheet("block")} />
        </RowGroup>
      </BottomSheet>
      {sheet === "booking" && <NewBookingSheet open onClose={() => setSheet(null)} initialDate={selected} initialVenueId={venueId} />}
      {sheet === "block" && <BlockSheet open onClose={() => setSheet(null)} initialDate={selected} initialVenueId={venueId} />}
      <BottomSheet
        open={Boolean(block)}
        onClose={() => setBlock(null)}
        title="Blocked dates"
        footer={
          <Button variant="destructive" onClick={removeBlock}>
            Remove block
          </Button>
        }
      >
        {block && (
          <Stack gap={2}>
            <ValidationSummary result={blockValidation.data} loading={blockValidation.isFetching} unavailable={blockValidation.isError} onRetry={() => void blockValidation.refetch()} />
            <Text>{block.kind === "maintenance" ? "Maintenance" : "Closed"}</Text>
            <Text variant="callout" tone="secondary">
              {venueById.get(block.venueId)?.name} · {fmtRange(block.startDate, block.endDate)}
            </Text>
            {block.note && <Text variant="callout">{block.note}</Text>}
          </Stack>
        )}
      </BottomSheet>
    </Page>
  );
}
